/* Theme checkout gate. The payment provider must also enforce the business rule. */
(() => {
  const invalidMessage = 'Não foi possível conferir o mínimo deste pack. Remova os itens do pack e monte-o novamente antes de finalizar.';
  const defaultMessage = 'Seu pack precisa de pelo menos {minimum} pacotinhos. Você tem {quantity}; adicione mais {remaining} ou remova todos os itens desse pack para continuar.';

  function evaluate(cart, messageTemplate = defaultMessage) {
    if (!Array.isArray(cart?.items)) return { blocked: true, message: 'Não foi possível conferir seu carrinho. Recarregue a página antes de finalizar.', groups: [] };
    const groups = new Map();
    let malformed = false;
    for (const item of cart.items) {
      const p = Array.isArray(item.properties) ? Object.fromEntries(item.properties) : item.properties || {};
      const rule = p._groovi_bundle_rule;
      const rawMinimum = p._groovi_bundle_minimum;
      if (!rule && (rawMinimum == null || rawMinimum === '')) continue;
      const minimum = Number(rawMinimum);
      if (!rule || !Number.isSafeInteger(minimum) || minimum < 1 || String(minimum) !== String(rawMinimum)
        || !Number.isSafeInteger(item.quantity) || item.quantity < 0) {
        malformed = true;
        continue;
      }
      const key = JSON.stringify([rule, minimum]);
      const group = groups.get(key) || { rule, minimum, quantity: 0 };
      group.quantity += item.quantity;
      groups.set(key, group);
    }
    const incomplete = [...groups.values()].filter((g) => g.quantity > 0 && g.quantity < g.minimum);
    const template = typeof messageTemplate === 'string' && messageTemplate.trim() ? messageTemplate.trim() : defaultMessage;
    const messages = incomplete.map((g) => template.replace(/\{(minimum|quantity|remaining)\}/g, (_, key) => ({ minimum: g.minimum, quantity: g.quantity, remaining: g.minimum - g.quantity })[key]));
    if (malformed) messages.unshift(invalidMessage);
    return { blocked: malformed || incomplete.length > 0, message: messages.join(' '), groups: [...groups.values()] };
  }

  const api = globalThis.GrooviBundleMinimum = { evaluate, busy: false };
  if (typeof document === 'undefined') return;
  const guards = () => [...document.querySelectorAll('groovi-bundle-minimum')];
  let revision = 0;
  const pending = new Set();

  class BundleMinimum extends HTMLElement {
    connectedCallback() {
      this.observer = new MutationObserver(() => this.readState());
      this.observer.observe(this, { childList: true, characterData: true, subtree: true });
      this.readState();
    }
    disconnectedCallback() { this.observer.disconnect(); }
    readState() {
      try { this.state = JSON.parse(this.querySelector('[data-bundle-minimum-state]').textContent); }
      catch { this.state = evaluate(null); }
      this.render();
    }
    setState(state) {
      const data = this.querySelector('[data-bundle-minimum-state]');
      const text = JSON.stringify({ blocked: state.blocked, message: state.message });
      if (data && data.textContent !== text) data.textContent = text;
      this.state = state;
      this.render();
    }
    render() {
      const blocked = Boolean(this.state?.blocked);
      this.dataset.blocked = String(blocked);
      const message = this.querySelector('[data-bundle-minimum-message]');
      const text = api.busy ? 'Atualizando as quantidades do seu carrinho…' : this.state?.message || '';
      if (message && message.textContent !== text) message.textContent = text;
      if (message) message.hidden = !api.busy && !blocked;
      for (const button of this.querySelectorAll('.cart__checkout-button')) {
        button.disabled = blocked || api.busy;
        button.setAttribute('aria-disabled', String(button.disabled));
      }
      const accelerated = this.querySelector('.additional-checkout-buttons');
      if (accelerated) {
        accelerated.hidden = blocked || api.busy;
        accelerated.inert = blocked || api.busy;
      }
    }
  }
  if (!customElements.get('groovi-bundle-minimum')) customElements.define('groovi-bundle-minimum', BundleMinimum);

  function focusMessage() {
    const message = guards().map((g) => g.querySelector('[data-bundle-minimum-message]')).find((m) => m && !m.hidden && m.getClientRects().length);
    message?.focus();
  }
  api.validate = (cart, { focus = false } = {}) => {
    // Never approve a stale cart while a quantity mutation is in flight.
    if (api.busy) { if (focus) focusMessage(); return false; }
    const state = evaluate(cart, guards()[0]?.dataset.messageTemplate);
    guards().forEach((g) => g.setState(state));
    if (state.blocked && focus) focusMessage();
    return !state.blocked;
  };
  api.isBlocked = () => api.busy || guards().some((g) => g.state?.blocked);

  async function refresh(version) {
    if (version !== revision) return;
    try {
      while (pending.size) await Promise.allSettled([...pending]);
      await Promise.allSettled(customElements.get('cart-items-component')?.pendingQuantityUpdates || []);
      if (version !== revision) return;
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (version !== revision) return;
      api.busy = false;
      api.validate(cart);
    } catch {
      if (version !== revision) return;
      api.busy = false;
      guards().forEach((g) => g.setState(evaluate(null)));
    }
  }
  function markBusy() {
    api.busy = true;
    guards().forEach((g) => g.render());
    return ++revision;
  }
  document.addEventListener('quantity-selector:update', (event) => {
    if (!event.target.closest?.('cart-items-component')) return;
    const version = markBusy();
    // Native cart changes are debounced by 300ms; close that pre-request gap too.
    setTimeout(() => refresh(version), 350);
  }, true);
  document.addEventListener('shopify:cart:lines-update', (event) => {
    const version = markBusy();
    if (event.promise) {
      pending.add(event.promise);
      event.promise.finally(() => pending.delete(event.promise)).catch(() => {});
    }
    refresh(version);
  }, true);
  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('.cart__checkout-button, .ymp_elem_mainBtn, .yampi-btn-finalize-single, a[href="/checkout"], a[href="/a/checkout"]')) return;
    if (!api.isBlocked()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    focusMessage();
  }, true);
  document.addEventListener('submit', (event) => {
    if (!event.submitter?.matches('[name="checkout"], .cart__checkout-button, .ymp_elem_mainBtn') || !api.isBlocked()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    focusMessage();
  }, true);
  addEventListener('pageshow', (event) => { if (event.persisted) refresh(markBusy()); });
})();
